// docs/116 CI6/CI8/CI12/CI13/CI16: what a hero in the inn has about them, drawn inside their figure (the
// view's `fk-village__hero`, whose `data-act` carries the same act for the CSS): a mug in hand (raised in a
// toast), a book open in the lap (a page turning now and then, its title in a plate over them), hands
// held out to the fire (or a poker in them), a hand down to the cat, an arm up for the throw, hands
// clapping in time to the bard, and asleep the zzz over the pillow. The bits that point (a mug, a hand)
// turn with the figure; the words do not.
import { useEffect, useState } from 'react';
import { playSfx } from '../../../../core/audio';
import type { HeroAct } from './store';
import type { DrinkKind } from './types';
import type { Sheets } from '../sprites';
import { useLife } from './life/store';
import { serverNow } from '../../../../core/time';
import { hashN } from '../layout';
import { keyNum } from '../scenery';

/** A reader turns a page this often (s). */
const PAGE_S = 12;
import { bookArt, mugArt, pokerArt } from './life/art';
import type { Pix as PixArt } from './paint';

export interface HeroBitsProps { heroKey: string; act?: HeroAct; mug?: DrinkKind; book?: string; scale: number; sheets?: Sheets; phase: number }

function Pix({ art, scale, className = '' }: { art: PixArt; scale: number; className?: string }) {
  return <span className={`fk-village__pix ${className}`} style={{ width: art.w * scale, height: art.h * scale, backgroundImage: `url(${art.url})`, backgroundSize: '100% 100%' }} />;
}

export function HeroBits({ heroKey, act, mug, book, scale }: HeroBitsProps): React.JSX.Element | null {
  const beat = useLife((s) => !!s.beat);
  // CI8: a reader turns a page now and then (docs/121: on the World's clock, so every view sees the same page turn)
  const [page, setPage] = useState(0);
  useEffect(() => {
    if (act !== 'read') return;
    const off = hashN(keyNum(heroKey), 0x9a6e) * PAGE_S;
    const pageAt = () => Math.floor((serverNow() / 1000 + off) / PAGE_S);
    const first = pageAt();
    const iv = window.setInterval(() => { const p = pageAt() - first; setPage((q) => { if (p > q) playSfx('inn.page', { gain: 0.35 }); return p; }); }, 500);
    return () => window.clearInterval(iv);
  }, [act, heroKey]);
  const clapping = beat && (!act || act === 'drink' || act === 'watch');
  const hands = act === 'warm' || act === 'pet' || act === 'cheer' || act === 'throw' || act === 'clap' || clapping;
  const s = scale;
  if (!act && !mug && !clapping) return null;
  return (
    <>
      <span className="fk-inn__bits" data-bits={act ?? (clapping ? 'clap' : 'mug')}>
        {mug && act !== 'sleep' && act !== 'read' && <span className={`fk-inn__hmug${act === 'toast' ? ' fk-inn__hmug--up' : ''}`}><Pix art={mugArt(mug)} scale={s} /></span>}
        {act === 'read' && <span className="fk-inn__book"><Pix art={bookArt()} scale={s} />{page > 0 && <span key={page} className="fk-inn__page" />}</span>}
        {act === 'poke' && <span className="fk-inn__poker"><Pix art={pokerArt()} scale={s} /></span>}
        {hands && <span className={`fk-inn__hands fk-inn__hands--${clapping && !act ? 'clap' : act}`}><span /><span /></span>}
      </span>
      {act === 'read' && book && <span className="fk-village__plate fk-inn__bookplate" data-testid="inn-book"><span className="fk-village__plate-sub">reading</span><span className="fk-village__plate-name">{book}</span></span>}
      {act === 'sleep' && <span className="fk-inn__zzz" aria-hidden="true"><span>z</span><span>z</span><span>Z</span></span>}
      {act === 'pet' && <span className="fk-inn__love" aria-hidden="true">♥</span>}
    </>
  );
}
